function _extends() { _extends = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends.apply(this, arguments); }

import React, { forwardRef, useMemo, useEffect, useImperativeHandle, useState, useRef } from 'react';
import { useFormFieldWithSelector, transformSchemaToJSON } from 'formydable';
import NoData from '../../Grid/NoData';
import Facade from '../../Facade';
import Loader from '../../Loader';
import { GridContext } from '../../Grid/hook';
import Grid from '../../Grid';
import { filterData, sortData, SortOrder } from '../../../common/utils/data';
import { useGridSwitching } from '../../../common/hooks/useGridSwitching';
import Icon from '../../Icon';
import TRManageDrag from './TRManageDrag';
import { useTable } from '../../../common/hooks/useTable';
import './index.scss';
import { useFormManager } from '../FormCore';
import classNames from 'classnames';
//@ts-ignore
const GridField = /*#__PURE__*/forwardRef(({
  name,
  schema,
  actions,
  value,
  asynchronous,
  loading: showFacade,
  draggableConfig,
  appendRow,
  rowAttr,
  children
}, ref) => {
  const {
    readonly
  } = useFormManager();
  const tableprops = useTable();
  const {
    filters
  } = tableprops;
  const {
    fieldState,
    setFieldArray,
    setFieldArrays,
    removeFieldArray,
    setDirty,
    setFieldValues
  } = useFormFieldWithSelector(name, true);
  const [loaderActive, setLoaderActive] = useState(false);
  const mounted = useRef(false);
  const defaultSchema = useMemo(() => {
    // removing render prop in schema 
    return Object.keys(schema).reduce((result, key) => {
      const {
        render,
        ...data
      } = schema[key];
      result[key] = data;
      return result;
    }, {}); // eslint-disable-next-line
  }, []);
  const states = fieldState;
  const isDraggable = !!(draggableConfig?.container && draggableConfig?.orderFieldName);
  const columnDisplay = Object.keys(schema).filter(key => !!schema[key].label && (schema[key].showColumnWhen ? schema[key].showColumnWhen() : true));
  const totalColumn = columnDisplay.length + (actions ? 1 : 0) + (isDraggable ? 1 : 0);
  const orderByListStates = isDraggable ? sortData({
    order: SortOrder.ASC,
    orderBy: `${draggableConfig?.orderFieldName}.value`
  }, states) : states;
  useGridSwitching({ ...draggableConfig,
    droppable: '.grid-field-container',
    draggable: '.grid-field-item',
    onDragEnded: event => {
      setFieldValues(event.reduce((result, each) => {
        result[`${name}[${each.dataIndex}].${draggableConfig?.orderFieldName}`] = each.listOrder;
        return result;
      }, {}));
    }
  });
  useImperativeHandle(ref, () => ({
    editRow: (item, index) => {
      setFieldValues(Object.keys(item).reduce((result, key) => {
        result[`${name}[${index}].${key}`] = item[key]; // setFieldValue(`${name}[${index}].${key}`, item[key])

        return result;
      }, {}));
      setDirty(true);
    },
    addRow: item => {
      const newRow = Object.keys(defaultSchema).reduce((result, key) => {
        result[key] = { ...defaultSchema[key],
          value: item[key] || defaultSchema[key].value
        };
        return result;
      }, {});
      setFieldArray(name, newRow, true);
      setDirty(true);
    },
    totalItems: states.length,
    getValueData: () => {
      return states.map(state => {
        return transformSchemaToJSON(state);
      });
    },
    reOrderItems: () => {
      if (isDraggable) {
        setFieldValues(orderByListStates.reduce((result, state, index) => {
          const dataIndex = states.findIndex(each => each === state);
          result[`${name}[${dataIndex}].${draggableConfig?.orderFieldName}`] = index + 1;
          return result;
        }, {}));
      }
    },
    removeRow: index => {
      removeFieldArray(name, index);
      setDirty(true);
    },
    removeAll: () => {
      removeFieldArray(name);
      setDirty(true);
    },
    updateAsync: () => {
      if (asynchronous) {
        setLoaderActive(true);
        setDirty(true);
      }
    } // eslint-disable-next-line

  }), [isDraggable, states]);
  useEffect(() => {
    if (value && value.length > 0) {
      const defaultArrayValues = value.map(item => {
        return Object.keys(defaultSchema).reduce((result, key) => {
          result[key] = { ...defaultSchema[key],
            value: item[key]
          };
          return result;
        }, {});
      });
      setFieldArrays(name, defaultArrayValues, true);
    }

    return () => removeFieldArray(name); // eslint-disable-next-line
  }, [value]);
  useEffect(() => {
    if (mounted.current) {
      setLoaderActive(false);
    } // eslint-disable-next-line

  }, [value]);
  useEffect(() => {
    mounted.current = true; // eslint-disable-next-line
  }, []);
  return /*#__PURE__*/React.createElement("div", {
    id: draggableConfig?.container,
    className: "grid-field grid-switching"
  }, /*#__PURE__*/React.createElement(GridContext.Provider, {
    value: tableprops
  }, children), /*#__PURE__*/React.createElement("table", {
    className: "table table-hover"
  }, /*#__PURE__*/React.createElement("thead", null, /*#__PURE__*/React.createElement("tr", null, isDraggable && /*#__PURE__*/React.createElement("th", {
    style: {
      width: 20
    }
  }), columnDisplay.map(key => /*#__PURE__*/React.createElement("th", {
    key: key,
    "data-column": key
  }, schema[key].label)), readonly !== true && actions && /*#__PURE__*/React.createElement("th", {
    className: "action-column"
  }, "Actions"))), /*#__PURE__*/React.createElement("tbody", {
    className: "grid-field-container"
  }, states.length > 0 ? filterData(filters, orderByListStates, '.value').map(state => {
    const index = states.findIndex(each => each === state);
    const transformedState = transformSchemaToJSON(state);
    const rowProps = rowAttr ? rowAttr(transformedState, index) : undefined;
    return /*#__PURE__*/React.createElement(TRManageDrag, _extends({}, rowProps, {
      key: index,
      "data-index": index,
      className: classNames("grid-field-item", null, rowProps?.className),
      onPointerDown: event => {
        if (!(event.target.matches('.grid-field-item-grab') || event.target.closest('.grid-field-item-grab'))) {
          event.stopPropagation();
        }
      }
    }), isDraggable && /*#__PURE__*/React.createElement("td", {
      className: "grid-field-item-grab"
    }, /*#__PURE__*/React.createElement(Icon, {
      name: "grip-vertical"
    })), columnDisplay.map((key, keyIndex) => {
      return /*#__PURE__*/React.createElement("td", {
        key: keyIndex,
        "data-column": key
      }, 'render' in schema[key] ? schema[key].render(transformedState[key], transformedState, index) : transformedState[key]);
    }), readonly !== true && (actions || isDraggable) && /*#__PURE__*/React.createElement("td", null, /*#__PURE__*/React.createElement("div", {
      className: "action-cell"
    }, actions?.filter(({
      showWhen
    }) => showWhen ? showWhen(transformedState) : true).map(({
      icon,
      title,
      className,
      onClick,
      children
    }, key) => /*#__PURE__*/React.createElement("button", {
      key: key,
      type: "button",
      title: title,
      className: `btn btn-sm${className ? ` ${className}` : ''}`,
      onClick: event => {
        onClick(event, transformedState, index);
      }
    }, children || /*#__PURE__*/React.createElement("i", {
      className: icon,
      "aria-hidden": "true"
    }))))));
  }) : asynchronous && showFacade && (!value || value.length === 0) ? new Array(5).fill(0).map((_, index) => /*#__PURE__*/React.createElement("tr", {
    key: index,
    "data-facade": true
  }, isDraggable && /*#__PURE__*/React.createElement("td", {
    className: "grid-item-grab"
  }, /*#__PURE__*/React.createElement(Icon, {
    name: "grip-vertical"
  })), [...Object.values(schema).filter(each => !!each.label), ...(readonly !== true && actions ? [{
    label: 'Actions'
  }] : [])].map((column, index) => {
    return /*#__PURE__*/React.createElement("td", {
      key: index
    }, /*#__PURE__*/React.createElement(Facade, {
      loading: true,
      rows: 1,
      padding: false,
      range: column.label === "Actions" ? [100, 100] : [Math.min(totalColumn * 15, 100), Math.min(totalColumn * 20, 100)]
    }));
  }))) : /*#__PURE__*/React.createElement(NoData, {
    totalColumn: totalColumn
  }), appendRow && appendRow(states, columnDisplay))), /*#__PURE__*/React.createElement(Loader, {
    active: loaderActive
  }));
});
GridField.Filter = Grid.Filter;
export default GridField;